import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Receipt20Regular } from '@fluentui/react-icons';
import ContentSkeleton from '../../ContentSkeleton/ContentSkeleton';
import TitledCard from '../../Layout/TitledCard';

import { actualFields } from '../actuals/types';
import { getActiveRoleFromToken } from '@moko/utils/src/authHelper';
import type {
  OrderBilling as Billing,
  Charges,
  FinalizeBilling,
  BillingSource,
  OrderBillingServices,
} from './types';

const useStyles = makeStyles({
  content: {
    display: 'grid',
    gap: tokens.spacingVerticalL,
    minWidth: 0,
    '& p': { margin: 0 },
    '& h4': { margin: 0 },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
});
function Breakdown({ charges }: { charges: Charges }) {
  return (
    <>
      <Text weight="semibold">Charges ({charges.currency})</Text>
      <Table aria-label={`Charges (${charges.currency})`} size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Item / basis</TableHeaderCell>
            <TableHeaderCell>Quantity</TableHeaderCell>
            <TableHeaderCell>Unit price</TableHeaderCell>
            <TableHeaderCell>Amount</TableHeaderCell>
            <TableHeaderCell>Rate and parameters</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {charges.breakdown.map((line, i) => (
            <TableRow key={i}>
              <TableCell>
                {line.name}
                <div>{line.description}</div>
                {line.percentageBaseAmount != null && (
                  <div>
                    Percentage basis: {line.percentageBaseAmount.toFixed(2)}
                  </div>
                )}
              </TableCell>
              <TableCell>
                {line.quantity}
                {line.measuredQuantity != null && (
                  <div>
                    Measured: {line.measuredQuantity} {line.quantityUnit}
                  </div>
                )}
                <div>{line.quantitySource}</div>
              </TableCell>
              <TableCell>{line.unitPrice.toFixed(2)}</TableCell>
              <TableCell>{line.amount.toFixed(2)}</TableCell>
              <TableCell>
                {line.rateSource}{' '}
                {line.rateId != null && `rate #${line.rateId}`}
                {line.appliedParameters?.map((p) => (
                  <div key={p.id}>
                    {p.key}: {p.value} ({p.source} #{p.id})
                  </div>
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Text weight="semibold">
        Total: {charges.price.toFixed(2)} {charges.currency}
      </Text>
    </>
  );
}
function Evidence({ source }: { source: BillingSource }) {
  return (
    <div>
      <p>Review reason: {source.data.reason}</p>
      <dl>
        {Object.entries(source.data.facts)
          .filter(([key, value]) => key !== 'expenses' && value != null)
          .map(([key, value]) => (
            <div key={key}>
              <dt>{actualFields[key as keyof typeof actualFields] ?? key}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
      </dl>
      {source.data.reviewedExpenses.map((e) => (
        <p key={e.id}>
          Receipt {e.id}: actual {e.actualAmount.toFixed(2)}, reimbursable{' '}
          {e.reimbursableAmount.toFixed(2)}, excess {e.excessAmount.toFixed(2)}{' '}
          {e.currency}
        </p>
      ))}
    </div>
  );
}
export default function OrderBilling({
  orderId,
  services,
}: {
  orderId: number;
  services: OrderBillingServices;
}) {
  const styles = useStyles();
  const [data, setData] = useState<Billing>();
  const [reason, setReason] = useState('');
  const [authorizeOverrun, setAuthorizeOverrun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const retry = useRef<FinalizeBilling | undefined>(undefined);
  const role = getActiveRoleFromToken();
  const allowed =
    role === 'FMSPortal.AdminAccess' || role === 'FMSManager.Access';
  useEffect(() => {
    const controller = new AbortController();
    setData(undefined);
    setReason('');
    setAuthorizeOverrun(false);
    setError('');
    setSuccess('');
    retry.current = undefined;
    if (allowed)
      services
        .load(controller, orderId)
        .then(setData)
        .catch((e) => {
          if (!controller.signal.aborted) setError(e.message);
        });
    return () => controller.abort();
  }, [orderId, allowed, services]);
  if (!allowed) return null;
  const refresh = async () => {
    setBusy(true);
    setError('');
    setSuccess('');
    setData(undefined);
    retry.current = undefined;
    try {
      setData(await services.load(new AbortController(), orderId));
      setAuthorizeOverrun(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const finalize = async () => {
    if (!data || busy) return;
    setBusy(true);
    setError('');
    setSuccess('');
    const request: FinalizeBilling = {
      operationId: '',
      expectedRevision: data.revision,
      expectedVersion: data.version,
      reason,
      authorizeOverrun,
    };
    // Keep the same operation ID when retrying the same request after a lost response.
    if (
      retry.current &&
      JSON.stringify({ ...retry.current, operationId: '' }) ===
        JSON.stringify(request)
    )
      request.operationId = retry.current.operationId;
    else request.operationId = crypto.randomUUID();
    retry.current = request;
    try {
      setData(await services.finalize(new AbortController(), orderId, request));
      setReason('');
      setAuthorizeOverrun(false);
      setSuccess('Charges finalized. Previous revisions remain in history.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const current = data?.history[data.history.length - 1];
  const alreadyFinalized = current?.data.version === data?.version;
  const overrun =
    data?.proposed &&
    data.estimate &&
    data.proposed.price > data.estimate.price;
  return (
    <section aria-label="Final billing">
      <TitledCard title="Final billing" icon={<Receipt20Regular />}>
        <div className={styles.content}>
          {error && (
            <MessageBar intent="error">
              <MessageBarBody>
                {error} Reload billing after a conflict to review the latest
                evidence.
              </MessageBarBody>
            </MessageBar>
          )}
          {success && !alreadyFinalized && (
            <MessageBar intent="success">
              <MessageBarBody>{success}</MessageBarBody>
            </MessageBar>
          )}
          {!data && !error && (
            <ContentSkeleton variant="form" rows={4} label="Loading billing…" />
          )}
          {data && (
            <>
              {alreadyFinalized && current && (
                <MessageBar intent="success">
                  <MessageBarBody>
                    <MessageBarTitle>Charges finalized</MessageBarTitle>
                    Approved total: {current.data.charges.price.toFixed(2)}{' '}
                    {current.data.charges.currency} · Revision{' '}
                    {current.revision}. The current Work evidence has been
                    finalized. A new charge revision requires corrected and
                    approved actuals.
                  </MessageBarBody>
                </MessageBar>
              )}
              <p>
                Pricing received:{' '}
                {data.pricing?.receivedAt ?? 'Unknown historical source'}
              </p>
              {data.blockers.map((b, i) => (
                <MessageBar key={i} intent="error">
                  <MessageBarBody>{b}</MessageBarBody>
                </MessageBar>
              ))}
              {data.estimate && (
                <details>
                  <summary>
                    Original estimate: {data.estimate.price.toFixed(2)}{' '}
                    {data.estimate.currency}
                  </summary>
                  <Breakdown charges={data.estimate} />
                </details>
              )}
              <Text weight="semibold" size={400}>
                Reviewed Work evidence
              </Text>
              {data.sources.map((s) => (
                <details key={s.workId}>
                  <summary>
                    <Link to={`/work?id=${s.workId}`}>Work {s.workId}</Link> —
                    approved revision {s.revision}
                  </summary>
                  <Evidence source={s} />
                </details>
              ))}
              {data.proposed && (
                <>
                  <Text weight="semibold" size={400}>
                    {alreadyFinalized
                      ? 'Finalized charges'
                      : 'Proposed final charges'}
                  </Text>
                  <Breakdown charges={data.proposed} />
                </>
              )}
              {!alreadyFinalized && (
                <>
                  <Field label="Review / correction reason">
                    <Textarea
                      maxLength={1000}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={busy}
                    />
                  </Field>
                  {overrun && (
                    <Checkbox
                      label="I authorize the amount above the original estimate for the stated reason."
                      checked={authorizeOverrun}
                      onChange={(_, checked) =>
                        setAuthorizeOverrun(checked.checked === true)
                      }
                      disabled={busy}
                    />
                  )}
                </>
              )}
              <Text weight="semibold" size={400}>
                Approved charge history
              </Text>
              <p>
                Only the latest approved revision contributes to final totals.
                Invoice integration is separate.
              </p>
              {current && (
                <p>
                  Active approved total: {current.data.charges.price.toFixed(2)}{' '}
                  {current.data.charges.currency}
                </p>
              )}
              {[...data.history].reverse().map((h) => (
                <details
                  key={h.revision}
                  open={alreadyFinalized && h.revision === current?.revision}
                >
                  <summary>
                    Revision {h.revision}
                    {h.revision === data.revision
                      ? ' (active)'
                      : ' (superseded)'}{' '}
                    — {h.data.charges.price.toFixed(2)}{' '}
                    {h.data.charges.currency}
                  </summary>
                  <p>
                    {h.createdAt} · Reviewer {h.createdBy} · {h.data.reason}
                  </p>
                  {h.supersedesRevision != null && (
                    <p>Supersedes revision {h.supersedesRevision}</p>
                  )}
                  {h.data.overrunAuthorized && (
                    <p>Estimate overrun explicitly authorized.</p>
                  )}
                  <Breakdown charges={h.data.charges} />
                  <details>
                    <summary>Source evidence</summary>
                    {h.data.sources.map((source) => (
                      <div key={source.workId}>
                        <p>
                          Work {source.workId}, approved revision{' '}
                          {source.revision}
                        </p>
                        <Evidence source={source} />
                      </div>
                    ))}
                  </details>
                </details>
              ))}
            </>
          )}
          <div className={styles.actions}>
            <Button disabled={busy} onClick={refresh}>
              Reload billing
            </Button>
            {data && !alreadyFinalized && (
              <Button
                appearance="primary"
                onClick={finalize}
                disabled={
                  busy ||
                  !data.proposed ||
                  data.blockers.length > 0 ||
                  !reason.trim() ||
                  (overrun && !authorizeOverrun) ||
                  alreadyFinalized
                }
              >
                {busy
                  ? 'Saving…'
                  : data.revision
                  ? 'Finalize corrected charges'
                  : 'Finalize charges'}
              </Button>
            )}
          </div>
        </div>
      </TitledCard>
    </section>
  );
}
